'use client';

import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

import { fetchJson } from 'lib/fetch-json';
import { EMAIL_REGEX, PASSWORD_REGEX } from 'lib/constants';

import { Trans, useTranslation } from 'src/i18n/client';
import Button from 'src/components/Button';
import Alert from 'src/components/Alert';
import FormError from 'src/components/FormError';
import useGlobalState from 'src/hooks/useGlobalState';
import useRecaptcha from 'src/hooks/useRecaptcha';
import { myAccountUrl } from 'src/services/route-utils';
import PasswordInput from 'src/components/PasswordInput';

export default function PageForm({ locale, redirect }: any) {
    const { t } = useTranslation(locale, 'common');
    const router = useRouter();
    const { setLoadUser } = useGlobalState();
    const { isReady, generateToken } = useRecaptcha();
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>();
    const {
        register,
        formState: { errors: _errors },
        handleSubmit,
        getValues,
    } = useForm();

    const errors: any = _errors;

    const onSubmit = async (data: any) => {
        setLoading(true);

        try {
            setErrorMsg('');
            const recaptcha = await generateToken();
            const body = {
                recaptcha,
                email: data.email,
                email_repeat: data.email_repeat,
                first_name: data.first_name,
                last_name: data.last_name,
                password: data.password,
                password_repeat: data.password_repeat,
            };

            await fetchJson(`/json/create-account?${new URLSearchParams({ locale })}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            await setLoadUser();
            const params = redirect ? new URLSearchParams({ redirect }) : '';
            router.push(myAccountUrl(`/confirmare-cont?${params}`));
        } catch (error: any) {
            setErrorMsg(error.data?.error?.message || t('unexpectedError'));
            setLoading(false);
        }
    };

    return (
        <div className="mx-auto text-center md:w-4/5 xl:w-2/3">
            <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <h1 className="text-5xl lg:text-6xl">{t('createAccount.heading')}</h1>

                <p className="my-4 text-sm">{t('createAccount.description')}</p>

                <div>
                    <input
                        id="first_name"
                        type="text"
                        autoFocus
                        disabled={loading}
                        className={errors.first_name ? 'form-error' : ''}
                        placeholder={t('createAccount.firstName')}
                        {...register('first_name', {
                            required: t('createAccount.requiredField'),
                        })}
                    />
                    <FormError error={errors.first_name} />
                </div>

                <div>
                    <input
                        id="last_name"
                        type="text"
                        disabled={loading}
                        className={errors.last_name ? 'form-error' : ''}
                        placeholder={t('createAccount.lastName')}
                        {...register('last_name', {
                            required: t('createAccount.requiredField'),
                        })}
                    />
                    <FormError error={errors.last_name} />
                </div>

                <div>
                    <input
                        id="email"
                        type="email"
                        disabled={loading}
                        className={errors.email ? 'form-error' : ''}
                        placeholder={t('createAccount.email')}
                        {...register('email', {
                            required: t('createAccount.requiredField'),
                            pattern: {
                                value: EMAIL_REGEX,
                                message: t('login.emailInvalid'),
                            },
                        })}
                    />
                    <FormError error={errors.email} />
                </div>

                <div>
                    <input
                        id="email_repeat"
                        type="email"
                        disabled={loading}
                        className={errors.email ? 'form-error' : ''}
                        placeholder={t('createAccount.emailRepeat')}
                        {...register('email_repeat', {
                            required: t('createAccount.requiredField'),
                            validate: (value) =>
                                (value && value === getValues('email')) || t('createAccount.emailRepeatMismatch'),
                        })}
                    />
                    <FormError error={errors.email_repeat} />
                </div>

                <div>
                    <PasswordInput
                        id="password"
                        disabled={loading}
                        className={errors.password ? 'form-error' : ''}
                        placeholder={t('createAccount.password')}
                        {...register('password', {
                            required: t('createAccount.requiredField'),
                            pattern: {
                                value: PASSWORD_REGEX,
                                message: t('activate.passwordInvalid'),
                            },
                        })}
                    />
                    <FormError error={errors.password} />
                </div>

                <div>
                    <PasswordInput
                        id="password_repeat"
                        disabled={loading}
                        className={errors.password_repeat ? 'form-error' : ''}
                        placeholder={t('createAccount.passwordRepeat')}
                        {...register('password_repeat', {
                            required: t('createAccount.requiredField'),
                            validate: (value) =>
                                (value && value === getValues('password')) || t('activate.passwordRepeatMismatch'),
                        })}
                    />
                    <FormError error={errors.password_repeat} />
                </div>

                <div className="text-left">
                    <label className="flex items-start gap-2">
                        <input
                            className="mt-1"
                            type="checkbox"
                            disabled={loading}
                            {...register('terms', {
                                required: t('activate.termsRequired'),
                            })}
                        />
                        <span>
                            <Trans
                                t={t}
                                i18nKey="common:activate.termsLabel"
                                components={{
                                    link: <Link className="underline hover:no-underline" href="/termeni-si-conditii" />,
                                }}
                            />
                        </span>
                    </label>
                    <FormError error={errors.terms} />
                </div>

                <Button variant="secondary" type="submit" disabled={loading} loading={loading}>
                    {t('createAccount.button')}
                </Button>

                {errorMsg && <Alert type="danger">{errorMsg}</Alert>}
            </form>

            <p className="my-4 text-center text-sm md:my-8">{t('createAccount.socialLogin')}</p>
        </div>
    );
}
